/* eslint-disable default-param-last */
import { AllowedReducer, InterfaceReducer } from '@leverege/api-redux';
import { AbstractItemDelegate } from '@leverege/molten/data-source';
import { History, RoutesUtil } from '@leverege/molten/routes';
import { GlobalState } from '@leverege/ui-redux';
import OpenApiApi from "../../openApiApi/OpenApiApi.js";
import OpenApiSortConverterFactory from "../../filters/sort/sortConverter/OpenApiSortConverterFactory.js";
import OpenApiGroupDelegate from './OpenApiGroupDelegate.js'; // eslint-disable-line import/no-cycle

export default class OpenApiItemDelegate extends AbstractItemDelegate {
  static TYPE = 'openApi.ItemDelegate';
  constructor(options) {
    super(options);
    this.type = options.delegateType ?? OpenApiItemDelegate.TYPE;
    const {
      match,
      relationship,
      id
    } = options;
    this.id = id;
    this.options = options;
    this.match = match;
    this.relationship = relationship;
    this.api = new OpenApiApi({
      relationship,
      match
    });
  }
  getApi() {
    return this.api;
  }
  parent() {
    const parent = this.relationship.containingGroup;
    if (parent == null) {
      return null;
    }

    // if parent is an item or an alias, use ItemDelegate
    if (parent.isItem || parent.isAlias) {
      return new OpenApiItemDelegate({
        ...this.options,
        relationship: parent,
        match: this.match
      });
    }
    return new OpenApiGroupDelegate({
      ...this.options,
      relationship: parent,
      match: this.match
    });
  }
  getInterfaceUrl() {
    return this.relationship.ref;
  }
  getUrl() {
    return RoutesUtil.resolve(this.relationship.urlPath, this.match);
  }
  getResourceId(forReducer, opts) {
    return forReducer ? RoutesUtil.resolve(this.relationship.urlPath, this.match) : this.relationship.refPath;
  }
  getParentListResourceId(forReducer, opts) {
    return forReducer ? RoutesUtil.resolve(this.relationship.parentRelatinship.urlPath, this.match) : this.relationship.parentRelationship.refPath;
  }
  requestCanGet() {
    return this.getApi().canGet(this.id);
  }
  get(opts, extra) {
    return this.getApi().get(opts, extra);
  }
  requestCanUpdate(patch, options) {
    return this.getApi().canUpdate(patch, options);
  }
  update(patch, options, extra) {
    return this.getApi().update(patch, options, extra);
  }
  requestCanDelete(deleteBody, options) {
    return this.getApi().canDelete(deleteBody, options);
  }
  delete(deleteBody, options, extra) {
    return this.getApi().delete(deleteBody, options, extra);
  }

  // utility

  goTo() {
    return History.push(`/${this.getUrl()}`);
  }
  isGotoAllowed() {
    return true;
  }

  // access

  getItem(state = GlobalState.get(), opts) {
    const res = InterfaceReducer.getObject(state, this.getResourceId(true, opts), opts, opts?.queryName);
    return res;
  }
  getStatus(state = GlobalState.get(), opts) {
    return InterfaceReducer.getStatus(state, this.getResourceId(true, opts));
  }
  isLoading(state = GlobalState.get(), opts) {
    return InterfaceReducer.isLoading(state, this.getResourceId(true, opts));
  }
  isDone(state = GlobalState.get(), opts) {
    return InterfaceReducer.isDone(state, this.getResourceId(true, opts));
  }
  canGet(state = GlobalState.get()) {
    if (!this.relationship.supportsRead) {
      return false;
    }
    return AllowedReducer.get(state, this.getResourceId(true))?.get;
  }
  canUpdate(state = GlobalState.get()) {
    if (!this.relationship.supportsUpdate) {
      return false;
    }
    return AllowedReducer.get(state, this.getResourceId(true))?.update;
  }
  canDelete(state = GlobalState.get()) {
    if (!this.relationship.supportsDelete) {
      return false;
    }
    return AllowedReducer.get(state, this.getResourceId(true))?.delete;
  }
  canAction(state = GlobalState.get(), action) {
    return AllowedReducer.get(state, this.getResourceId(true))?.[action];
  }
  requestCanAction(body, options, getPath, method, action) {
    return this.getApi().canAction(body, options, getPath, method, action);
  }
}